import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import {
  useGenerateCampaignMutation,
  useGetGenerateCampaignResultQuery,
} from "../../api";
import { RestartIcon } from "../images";
import { Fields } from "./ProductDetailsModal";
import { AnalysisStatus, SSEEvents } from "../../types/companyTypes";
import { setStatus } from "../../features/companySlice";
import { useAppDispatch, useAppSelector } from "../../hooks/useReduxHooks";

type Props = {
  pdfFiles: File[];
  productUrl: string;
  campaign_id: string;
  setResultHandler: (result: Fields) => void;
  setIsAnalysis: (isAnalysis: boolean) => void;
};

export const AnalyzingProductBlock = ({
  pdfFiles,
  campaign_id,
  setResultHandler,
  productUrl,
  setIsAnalysis,
}: Props) => {
  const dispatch = useAppDispatch();
  const { generation_status } = useAppSelector((state) => state.company.status);

  const [getResult, setGetResult] = useState<boolean>(false);
  const [startAnalyze, setStartAnalyze] = useState<boolean>(false);
  const [currentProgress, setCurrentProgress] = useState<number>(0);

  const [generateCampaign, { data: generateData }] =
    useGenerateCampaignMutation();

  const { data: generateCampaignResult } = useGetGenerateCampaignResultQuery(
    generateData?.task_id || "",
    {
      skip: !getResult || !generateData?.task_id,
      refetchOnMountOrArgChange: true,
    },
  );

  useEffect(() => {
    if (startAnalyze) {
      generateCampaign({
        url: productUrl || undefined,
        pdf_files: pdfFiles,
      });
    }
    setIsAnalysis(startAnalyze);
  }, [startAnalyze, campaign_id]);

  useEffect(() => {
    if (generateData?.task_id === generation_status?.task_id) {
      if (generation_status?.status === AnalysisStatus.failed) {
        setStartAnalyze(false);
        dispatch(setStatus({ [SSEEvents.generation_status]: undefined }));
      }

      setCurrentProgress(generation_status?.progress || 0);

      if (generation_status?.progress === 100) {
        setGetResult(true);
      }
    }
  }, [generation_status, generateData]);

  useEffect(() => {
    if (generateCampaignResult) {
      setResultHandler(generateCampaignResult);
      setStartAnalyze(false);
      setGetResult(false);
      dispatch(setStatus({ [SSEEvents.generation_status]: undefined }));
    }
  }, [generateCampaignResult]);

  return startAnalyze ? (
    <Box display="flex" alignItems="center" justifyContent="center" gap={2}>
      <Stack direction="row" gap="8px">
        <Typography
          color="#1D3E70"
          sx={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          Analyzing the product:{" "}
        </Typography>
        <Typography
          color="#1D3E70"
          sx={{
            minWidth: "30px",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {`${currentProgress}%`}
        </Typography>
        <CircularProgress size={24} sx={{ color: "custom.subTitleModal" }} />
      </Stack>
    </Box>
  ) : (
    <Stack
      direction="row"
      sx={{ cursor: "pointer" }}
      gap="4px"
      onClick={() => setStartAnalyze(true)}
    >
      <RestartIcon />
      <Typography
        color="#1D3E70"
        sx={{
          textOverflow: "ellipsis",
          overflow: "hidden",
          whiteSpace: "nowrap",
        }}
      >
        Restart Analysis
      </Typography>
    </Stack>
  );
};
